import * as React10 from 'react';
import React10__default, { createContext, memo, isValidElement, useState, useContext, useCallback, useRef, useEffect, useMemo } from 'react';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Slot } from '@radix-ui/react-slot';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { Check, X, ChevronRight, Circle, ChevronDown, ChevronUp, CheckIcon, CopyIcon, MoreHorizontal, ArrowDownIcon, XIcon, SendIcon, ChevronsLeft, ChevronLeft, ChevronsRight, HelpCircle, Package, Copy, Download, CheckCircle, CheckCircle2, FileTextIcon, IndentDecreaseIcon, IndentIncreaseIcon, AlertCircle, Info, AlertTriangle, AlignJustifyIcon, AlignRightIcon, AlignCenterIcon, AlignLeftIcon, XCircle, Heading1, Heading2, Heading3, List, ListOrdered, Bold, Italic, Underline, Strikethrough, Code } from 'lucide-react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as ToastPrimitives from '@radix-ui/react-toast';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { Prism } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';
import hardenReactMarkdown from 'harden-react-markdown';
import { useReactTable, getPaginationRowModel, getSortedRowModel, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { $isCodeNode, $createCodeNode, CodeNode } from '@lexical/code';
import { LinkNode, $isLinkNode } from '@lexical/link';
import { ListNode, ListItemNode, INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list';
import { HeadingNode, QuoteNode, $createHeadingNode } from '@lexical/rich-text';
import { $getRoot, $createTextNode, SELECTION_CHANGE_COMMAND, COMMAND_PRIORITY_CRITICAL, ParagraphNode, TextNode, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND, FORMAT_ELEMENT_COMMAND, $getSelection, $isRangeSelection, $createParagraphNode, FORMAT_TEXT_COMMAND, $isElementNode } from 'lexical';
import { CHECK_LIST, LINK, ELEMENT_TRANSFORMERS, MULTILINE_ELEMENT_TRANSFORMERS, TEXT_FORMAT_TRANSFORMERS, TEXT_MATCH_TRANSFORMERS, $convertFromMarkdownString, $convertToMarkdownString } from '@lexical/markdown';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable as ContentEditable$1 } from '@lexical/react/LexicalContentEditable';
import { $findMatchingParent } from '@lexical/utils';
import { $setBlocksType, $isAtNodeEnd } from '@lexical/selection';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import * as TogglePrimitive from '@radix-ui/react-toggle';

// src/components/ui/alert.tsx
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Alert = React10.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
var AlertTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "h5",
  {
    ref,
    className: cn("mb-1 font-medium leading-none tracking-tight", className),
    ...props
  }
));
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("text-sm [&_p]:leading-relaxed", className),
    ...props
  }
));
AlertDescription.displayName = "AlertDescription";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React10.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
var AlertDialogPortal = AlertDialogPrimitive.Portal;
var AlertDialogOverlay = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;
var AlertDialogContent = React10.forwardRef(({ className, portalContainer, overlayClassName, ...props }, ref) => /* @__PURE__ */ jsxs(AlertDialogPortal, { container: portalContainer, children: [
  /* @__PURE__ */ jsx(AlertDialogOverlay, { className: overlayClassName }),
  /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props
    }
  )
] }));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;
var AlertDialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
AlertDialogHeader.displayName = "AlertDialogHeader";
var AlertDialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
AlertDialogFooter.displayName = "AlertDialogFooter";
var AlertDialogTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold", className),
    ...props
  }
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;
var AlertDialogDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;
var AlertDialogAction = React10.forwardRef(({ className, variant, size, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Action,
  {
    ref,
    className: cn(buttonVariants({ variant, size }), className),
    ...props
  }
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;
var AlertDialogCancel = React10.forwardRef(({ className, variant = "outline", size, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Cancel,
  {
    ref,
    className: cn(
      buttonVariants({ variant, size }),
      "mt-2 sm:mt-0",
      className
    ),
    ...props
  }
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;
var Avatar = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    ),
    ...props
  }
));
Avatar.displayName = AvatarPrimitive.Root.displayName;
var AvatarImage = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Image,
  {
    ref,
    className: cn("aspect-square h-full w-full", className),
    ...props
  }
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
var AvatarFallback = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "flex h-full w-full items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
var badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Badge = React10.forwardRef(
  ({ className, variant, ...props }, ref) => {
    return /* @__PURE__ */ jsx("div", { ref, className: cn(badgeVariants({ variant }), className), ...props });
  }
);
Badge.displayName = "Badge";
var Breadcrumb = React10.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx("nav", { ref, "aria-label": "breadcrumb", ...props }));
Breadcrumb.displayName = "Breadcrumb";
var BreadcrumbList = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "ol",
  {
    ref,
    className: cn(
      "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
      className
    ),
    ...props
  }
));
BreadcrumbList.displayName = "BreadcrumbList";
var BreadcrumbItem = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "li",
  {
    ref,
    className: cn("inline-flex items-center gap-1.5", className),
    ...props
  }
));
BreadcrumbItem.displayName = "BreadcrumbItem";
var BreadcrumbLink = React10.forwardRef(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      ref,
      className: cn("transition-colors hover:text-foreground", className),
      ...props
    }
  );
});
BreadcrumbLink.displayName = "BreadcrumbLink";
var BreadcrumbPage = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "span",
  {
    ref,
    role: "link",
    "aria-disabled": "true",
    "aria-current": "page",
    className: cn("font-normal text-foreground", className),
    ...props
  }
));
BreadcrumbPage.displayName = "BreadcrumbPage";
var BreadcrumbSeparator = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "li",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("[&>svg]:w-3.5 [&>svg]:h-3.5", className),
    ...props,
    children: children ?? /* @__PURE__ */ jsx(ChevronRight, {})
  }
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
var BreadcrumbEllipsis = ({
  className,
  ...props
}) => /* @__PURE__ */ jsxs(
  "span",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("flex h-9 w-9 items-center justify-center", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" }),
      /* @__PURE__ */ jsx("span", { className: "sr-only", children: "More" })
    ]
  }
);
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis";
var Card = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn(
      "rounded-xl border bg-card text-card-foreground shadow",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("font-semibold leading-none tracking-tight", className),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";
var Checkbox = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  CheckboxPrimitive.Root,
  {
    ref,
    className: cn(
      "peer h-4 w-4 shrink-0 rounded-sm border border-primary shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(
      CheckboxPrimitive.Indicator,
      {
        className: cn("flex items-center justify-center text-current"),
        children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" })
      }
    )
  }
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
var Collapsible = CollapsiblePrimitive.Root;
var CollapsibleTrigger2 = CollapsiblePrimitive.CollapsibleTrigger;
var CollapsibleContent2 = CollapsiblePrimitive.CollapsibleContent;
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(DialogPortal, { children: [
  /* @__PURE__ */ jsx(DialogOverlay, {}),
  /* @__PURE__ */ jsxs(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground", children: [
          /* @__PURE__ */ jsx(X, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
var DropdownMenuSubTrigger = React10.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronRight, { className: "ml-auto" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var DropdownMenuContent = React10.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = React10.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuCheckboxItem = React10.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuRadioItem = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Circle, { className: "h-2 w-2 fill-current" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
var DropdownMenuLabel = React10.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("ml-auto text-xs tracking-widest opacity-60", className),
      ...props
    }
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
var Input = React10.forwardRef(
  ({ className, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "input",
      {
        type,
        className: cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";
var labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label2 = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label2.displayName = LabelPrimitive.Root.displayName;
var Progress = React10.forwardRef(({ className, value, ...props }, ref) => /* @__PURE__ */ jsx(
  ProgressPrimitive.Root,
  {
    ref,
    className: cn(
      "relative h-2 w-full overflow-hidden rounded-full bg-primary/20",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(
      ProgressPrimitive.Indicator,
      {
        className: "h-full w-full flex-1 bg-primary transition-all",
        style: { transform: `translateX(-${100 - (value || 0)}%)` }
      }
    )
  }
));
Progress.displayName = ProgressPrimitive.Root.displayName;
var RadioGroup2 = React10.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Root,
    {
      className: cn("grid gap-2", className),
      ...props,
      ref
    }
  );
});
RadioGroup2.displayName = RadioGroupPrimitive.Root.displayName;
var RadioGroupItem = React10.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "aspect-square h-4 w-4 rounded-full border border-primary text-primary shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx(Circle, { className: "h-3.5 w-3.5 fill-primary" }) })
    }
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
var ScrollArea = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  ScrollAreaPrimitive.Root,
  {
    ref,
    className: cn("relative overflow-hidden", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx(ScrollAreaPrimitive.Viewport, { className: "h-full w-full rounded-[inherit]", children }),
      /* @__PURE__ */ jsx(ScrollBar, {}),
      /* @__PURE__ */ jsx(ScrollAreaPrimitive.Corner, {})
    ]
  }
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = React10.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;
function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  disabled = false
}) {
  const [open, setOpen] = React10.useState(false);
  const [searchQuery, setSearchQuery] = React10.useState("");
  const containerRef = React10.useRef(null);
  const selectedOption = options.find((opt) => opt.value === value);
  const filteredOptions = options.filter(
    (opt) => opt.label.toLowerCase().includes(searchQuery.toLowerCase()) || opt.value.toLowerCase().includes(searchQuery.toLowerCase())
  );
  React10.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);
  const handleSelect = (optionValue) => {
    onValueChange(optionValue);
    setOpen(false);
    setSearchQuery("");
  };
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: "relative", children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-accent/50"
        ),
        onClick: () => !disabled && setOpen(!open),
        children: [
          /* @__PURE__ */ jsx("span", { className: cn(!selectedOption && "text-muted-foreground"), children: selectedOption ? selectedOption.label : placeholder }),
          /* @__PURE__ */ jsx(ChevronDown, { className: cn("h-4 w-4 opacity-50 transition-transform", open && "rotate-180") })
        ]
      }
    ),
    open && /* @__PURE__ */ jsxs("div", { className: "absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md", children: [
      /* @__PURE__ */ jsx("div", { className: "p-2", children: /* @__PURE__ */ jsx(
        Input,
        {
          placeholder: searchPlaceholder,
          value: searchQuery,
          onChange: (e) => setSearchQuery(e.target.value),
          className: "h-8",
          autoFocus: true
        }
      ) }),
      /* @__PURE__ */ jsx(ScrollArea, { className: "max-h-[300px]", children: /* @__PURE__ */ jsx("div", { className: "p-1", children: filteredOptions.length === 0 ? /* @__PURE__ */ jsx("div", { className: "py-6 text-center text-sm text-muted-foreground", children: emptyMessage }) : filteredOptions.map((option) => /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
            value === option.value && "bg-accent"
          ),
          onClick: () => handleSelect(option.value),
          children: [
            /* @__PURE__ */ jsx(
              Check,
              {
                className: cn(
                  "mr-2 h-4 w-4",
                  value === option.value ? "opacity-100" : "opacity-0"
                )
              }
            ),
            option.label
          ]
        },
        option.value
      )) }) })
    ] })
  ] });
}
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 opacity-50" }) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(ChevronUp, { className: "h-4 w-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React10.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx(
        SelectPrimitive.Viewport,
        {
          className: cn(
            "p-1",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("px-2 py-1.5 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute right-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) }) }),
      /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
var Separator3 = React10.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator3.displayName = SeparatorPrimitive.Root.displayName;
var Slider = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(
  SliderPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex w-full touch-none select-none items-center",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx(SliderPrimitive.Track, { className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20", children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: "absolute h-full bg-primary" }) }),
      /* @__PURE__ */ jsx(SliderPrimitive.Thumb, { className: "block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" })
    ]
  }
));
Slider.displayName = SliderPrimitive.Root.displayName;
var Switch = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SwitchPrimitives.Root,
  {
    className: cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;
var Table = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { className: "relative w-full overflow-auto", children: /* @__PURE__ */ jsx(
  "table",
  {
    ref,
    className: cn("w-full caption-bottom text-sm", className),
    ...props
  }
) }));
Table.displayName = "Table";
var TableHeader = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("thead", { ref, className: cn("[&_tr]:border-b", className), ...props }));
TableHeader.displayName = "TableHeader";
var TableBody = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tbody",
  {
    ref,
    className: cn("[&_tr:last-child]:border-0", className),
    ...props
  }
));
TableBody.displayName = "TableBody";
var TableFooter = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tfoot",
  {
    ref,
    className: cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    ),
    ...props
  }
));
TableFooter.displayName = "TableFooter";
var TableRow = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tr",
  {
    ref,
    className: cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    ),
    ...props
  }
));
TableRow.displayName = "TableRow";
var TableHead = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "th",
  {
    ref,
    className: cn(
      "h-10 px-2 text-left align-middle font-medium text-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    ),
    ...props
  }
));
TableHead.displayName = "TableHead";
var TableCell = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "td",
  {
    ref,
    className: cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    ),
    ...props
  }
));
TableCell.displayName = "TableCell";
var TableCaption = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "caption",
  {
    ref,
    className: cn("mt-4 text-sm text-muted-foreground", className),
    ...props
  }
));
TableCaption.displayName = "TableCaption";
var Tabs = TabsPrimitive.Root;
var TabsList = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
var Textarea = React10.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    "textarea",
    {
      className: cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      ),
      ref,
      ...props
    }
  );
});
Textarea.displayName = "Textarea";
var ToastProvider = ToastPrimitives.Provider;
var ToastViewport = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Viewport,
  {
    ref,
    className: cn(
      "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    ),
    ...props
  }
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
var toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        destructive: "destructive group border-destructive bg-destructive text-destructive-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Toast = React10.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    ToastPrimitives.Root,
    {
      ref,
      className: cn(toastVariants({ variant }), className),
      ...props
    }
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;
var ToastAction = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Action,
  {
    ref,
    className: cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    ),
    ...props
  }
));
ToastAction.displayName = ToastPrimitives.Action.displayName;
var ToastClose = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Close,
  {
    ref,
    className: cn(
      "absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    ),
    "toast-close": "",
    ...props,
    children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
  }
));
ToastClose.displayName = ToastPrimitives.Close.displayName;
var ToastTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Title,
  {
    ref,
    className: cn("text-sm font-semibold [&+div]:text-xs", className),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
var ToastDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Description,
  {
    ref,
    className: cn("text-sm opacity-90", className),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;
var TOAST_LIMIT = 1;
var TOAST_REMOVE_DELAY = 1e6;
var count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}
var toastTimeouts = /* @__PURE__ */ new Map();
var addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
var reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
var listeners = [];
var memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}
function toast({ ...props }) {
  const id = genId();
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open)
          dismiss();
      }
    }
  });
  return {
    id,
    dismiss,
    update
  };
}
function useToast() {
  const [state, setState] = React10.useState(memoryState);
  React10.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
}
function Toaster() {
  const { toasts } = useToast();
  return /* @__PURE__ */ jsxs(ToastProvider, { children: [
    toasts.map(function({ id, title, description, action, ...props }) {
      return /* @__PURE__ */ jsxs(Toast, { ...props, children: [
        /* @__PURE__ */ jsxs("div", { className: "grid gap-1", children: [
          title && /* @__PURE__ */ jsx(ToastTitle, { children: title }),
          description && /* @__PURE__ */ jsx(ToastDescription, { children: description })
        ] }),
        action,
        /* @__PURE__ */ jsx(ToastClose, {})
      ] }, id);
    }),
    /* @__PURE__ */ jsx(ToastViewport, {})
  ] });
}
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React10.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
      className
    ),
    ...props
  }
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
var Conversation = ({ className, ...props }) => /* @__PURE__ */ jsx(
  StickToBottom,
  {
    className: cn("relative flex-1 min-h-0", className),
    initial: "smooth",
    resize: "smooth",
    role: "log",
    ...props
  }
);
var ConversationContent = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(StickToBottom.Content, { className: cn("p-4", className), ...props });
var ConversationScrollButton = ({
  className,
  ...props
}) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();
  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);
  return !isAtBottom && /* @__PURE__ */ jsx(
    Button,
    {
      className: cn(
        "absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full",
        className
      ),
      onClick: handleScrollToBottom,
      size: "icon",
      type: "button",
      variant: "outline",
      ...props,
      children: /* @__PURE__ */ jsx(ArrowDownIcon, { className: "size-4" })
    }
  );
};
var Message = ({ className, from, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "group flex w-full items-end justify-end gap-2 py-4",
      from === "user" ? "is-user" : "is-assistant flex-row-reverse justify-end",
      "[&>div]:max-w-[80%]",
      className
    ),
    ...props
  }
);
var MessageContent = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col gap-2 overflow-hidden rounded-lg px-4 py-3 text-foreground text-sm",
      "group-[.is-user]:bg-primary group-[.is-user]:text-primary-foreground",
      "group-[.is-assistant]:bg-secondary group-[.is-assistant]:text-foreground",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: "is-user:dark", children })
  }
);
var MessageAvatar = ({
  src,
  name,
  className
}) => /* @__PURE__ */ jsxs(Avatar, { className: cn("size-8", className), children: [
  /* @__PURE__ */ jsx(AvatarImage, { src, alt: name }),
  /* @__PURE__ */ jsx(AvatarFallback, { children: name?.slice(0, 2).toUpperCase() || "AI" })
] });
var PromptInput = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "form",
  {
    className: cn(
      "w-full divide-y overflow-hidden rounded-xl border bg-background shadow-sm",
      className
    ),
    ...props
  }
);
var PromptInputTextarea = ({
  onChange,
  className,
  placeholder = "What would you like to know?",
  minHeight = 48,
  maxHeight = 164,
  ...props
}) => {
  const textareaRef = useRef(null);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea)
      return;
    const handleInput = () => {
      textarea.style.height = "auto";
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    };
    textarea.addEventListener("input", handleInput);
    handleInput();
    return () => {
      textarea.removeEventListener("input", handleInput);
    };
  }, [minHeight, maxHeight]);
  return /* @__PURE__ */ jsx(
    Textarea,
    {
      ref: textareaRef,
      className: cn(
        "w-full resize-none rounded-none border-0 bg-transparent p-4 shadow-none focus-visible:ring-0",
        className
      ),
      placeholder,
      onChange,
      onKeyDown: handleKeyDown,
      style: { minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px` },
      ...props
    }
  );
};
var PromptInputToolbar = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn("flex items-center justify-between gap-2 p-2", className),
    ...props
  }
);
var PromptInputTools = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx("div", { className: cn("flex items-center gap-1", className), ...props });
var PromptInputSubmit = ({
  className,
  status = "ready",
  disabled,
  ...props
}) => {
  const isStreaming = status === "streaming";
  return /* @__PURE__ */ jsx(
    Button,
    {
      className: cn("ml-auto", className),
      disabled: disabled || isStreaming,
      size: "icon",
      type: "submit",
      ...props,
      children: isStreaming ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(XIcon, { className: "size-4" }),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Stop" })
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(SendIcon, { className: "size-4" }),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Send" })
      ] })
    }
  );
};
var PromptInputButton = ({
  className,
  size = "sm",
  variant = "ghost",
  ...props
}) => /* @__PURE__ */ jsx(
  Button,
  {
    className: cn("h-8", className),
    size,
    variant,
    type: "button",
    ...props
  }
);
var CodeBlockContext = createContext({
  code: ""
});
var CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  className,
  children,
  ...props
}) => /* @__PURE__ */ jsx(CodeBlockContext.Provider, { value: { code }, children: /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "relative w-full overflow-hidden rounded-md border bg-background text-foreground",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(
        Prism,
        {
          className: "overflow-hidden dark:hidden",
          codeTagProps: {
            className: "font-mono text-sm"
          },
          customStyle: {
            margin: 0,
            padding: "1rem",
            fontSize: "0.875rem",
            background: "hsl(var(--background))",
            color: "hsl(var(--foreground))"
          },
          language,
          lineNumberStyle: {
            color: "hsl(var(--muted-foreground))",
            paddingRight: "1rem",
            minWidth: "2.5rem"
          },
          showLineNumbers,
          style: oneLight,
          children: code
        }
      ),
      /* @__PURE__ */ jsx(
        Prism,
        {
          className: "hidden overflow-hidden dark:block",
          codeTagProps: {
            className: "font-mono text-sm"
          },
          customStyle: {
            margin: 0,
            padding: "1rem",
            fontSize: "0.875rem",
            background: "hsl(var(--background))",
            color: "hsl(var(--foreground))"
          },
          language,
          lineNumberStyle: {
            color: "hsl(var(--muted-foreground))",
            paddingRight: "1rem",
            minWidth: "2.5rem"
          },
          showLineNumbers,
          style: oneDark,
          children: code
        }
      ),
      children && /* @__PURE__ */ jsx("div", { className: "absolute right-2 top-2 flex items-center gap-2", children })
    ] })
  }
) });
var CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2e3,
  children,
  className,
  ...props
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const { code } = useContext(CodeBlockContext);
  const copyToClipboard = async () => {
    if (typeof window === "undefined" || !navigator.clipboard.writeText) {
      onError?.(new Error("Clipboard API not available"));
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      onCopy?.();
      setTimeout(() => setIsCopied(false), timeout);
    } catch (error) {
      onError?.(error);
    }
  };
  const Icon2 = isCopied ? CheckIcon : CopyIcon;
  return /* @__PURE__ */ jsx(
    Button,
    {
      className: cn("shrink-0", className),
      onClick: copyToClipboard,
      size: "icon",
      variant: "ghost",
      ...props,
      children: children ?? /* @__PURE__ */ jsx(Icon2, { size: 14 })
    }
  );
};
function parseIncompleteMarkdown(text) {
  if (!text || typeof text !== "string") {
    return text;
  }
  let result = text;
  const linkImagePattern = /(!?\[)([^\]]*?)$/;
  const linkMatch = result.match(linkImagePattern);
  if (linkMatch) {
    const startIndex = result.lastIndexOf(linkMatch[1]);
    result = result.substring(0, startIndex);
  }
  const boldPattern = /(\*\*)([^*]*?)$/;
  if (boldPattern.test(result)) {
    const asteriskPairs = (result.match(/\*\*/g) || []).length;
    if (asteriskPairs % 2 === 1) {
      result = `${result}**`;
    }
  }
  const italicPattern = /(__)([^_]*?)$/;
  if (italicPattern.test(result)) {
    const underscorePairs = (result.match(/__/g) || []).length;
    if (underscorePairs % 2 === 1) {
      result = `${result}__`;
    }
  }
  const singleAsteriskPattern = /(\*)([^*]*?)$/;
  if (singleAsteriskPattern.test(result)) {
    const singleAsterisks = result.split("").reduce((acc, char, index) => {
      if (char === "*") {
        const prevChar = result[index - 1];
        const nextChar = result[index + 1];
        if (prevChar !== "*" && nextChar !== "*") {
          return acc + 1;
        }
      }
      return acc;
    }, 0);
    if (singleAsterisks % 2 === 1) {
      result = `${result}*`;
    }
  }
  const singleUnderscorePattern = /(_)([^_]*?)$/;
  if (singleUnderscorePattern.test(result)) {
    const singleUnderscores = result.split("").reduce((acc, char, index) => {
      if (char === "_") {
        const prevChar = result[index - 1];
        const nextChar = result[index + 1];
        if (prevChar !== "_" && nextChar !== "_") {
          return acc + 1;
        }
      }
      return acc;
    }, 0);
    if (singleUnderscores % 2 === 1) {
      result = `${result}_`;
    }
  }
  const inlineCodePattern = /(`)([^`]*?)$/;
  if (inlineCodePattern.test(result)) {
    const codeBlockPattern = /```[\s\S]*?```/g;
    const completeCodeBlocks = (result.match(codeBlockPattern) || []).length;
    const allTripleBackticks = (result.match(/```/g) || []).length;
    const insideIncompleteCodeBlock = allTripleBackticks % 2 === 1 && completeCodeBlocks * 2 !== allTripleBackticks;
    if (!insideIncompleteCodeBlock) {
      let singleBacktickCount = 0;
      for (let i = 0; i < result.length; i++) {
        if (result[i] === "`") {
          const isTripleStart = result.substring(i, i + 3) === "```";
          const isTripleMiddle = i > 0 && result.substring(i - 1, i + 2) === "```";
          const isTripleEnd = i > 1 && result.substring(i - 2, i + 1) === "```";
          if (!(isTripleStart || isTripleMiddle || isTripleEnd)) {
            singleBacktickCount++;
          }
        }
      }
      if (singleBacktickCount % 2 === 1) {
        result += "`";
      }
    }
  }
  const strikethroughPattern = /(~~)([^~]*?)$/;
  if (strikethroughPattern.test(result)) {
    const tildePairs = (result.match(/~~/g) || []).length;
    if (tildePairs % 2 === 1) {
      result = `${result}~~`;
    }
  }
  return result;
}
var HardenedMarkdown = hardenReactMarkdown(ReactMarkdown);
var components = {
  ol: ({ children, className, ...props }) => /* @__PURE__ */ jsx("ol", { className: cn("ml-4 list-outside list-decimal", className), ...props, children }),
  li: ({ children, className, ...props }) => /* @__PURE__ */ jsx("li", { className: cn("py-1", className), ...props, children }),
  ul: ({ children, className, ...props }) => /* @__PURE__ */ jsx("ul", { className: cn("ml-4 list-outside list-disc", className), ...props, children }),
  hr: ({ className, ...props }) => /* @__PURE__ */ jsx("hr", { className: cn("my-6 border-border", className), ...props }),
  strong: ({ children, className, ...props }) => /* @__PURE__ */ jsx("span", { className: cn("font-semibold", className), ...props, children }),
  a: ({ children, className, ...props }) => /* @__PURE__ */ jsx(
    "a",
    {
      className: cn("font-medium text-primary underline", className),
      rel: "noreferrer",
      target: "_blank",
      ...props,
      children
    }
  ),
  h1: ({ children, className, ...props }) => /* @__PURE__ */ jsx(
    "h1",
    {
      className: cn("mt-6 mb-2 text-3xl font-semibold", className),
      ...props,
      children
    }
  ),
  h2: ({ children, className, ...props }) => /* @__PURE__ */ jsx(
    "h2",
    {
      className: cn("mt-6 mb-2 text-2xl font-semibold", className),
      ...props,
      children
    }
  ),
  h3: ({ children, className, ...props }) => /* @__PURE__ */ jsx("h3", { className: cn("mt-6 mb-2 text-xl font-semibold", className), ...props, children }),
  h4: ({ children, className, ...props }) => /* @__PURE__ */ jsx("h4", { className: cn("mt-6 mb-2 text-lg font-semibold", className), ...props, children }),
  h5: ({ children, className, ...props }) => /* @__PURE__ */ jsx(
    "h5",
    {
      className: cn("mt-6 mb-2 text-base font-semibold", className),
      ...props,
      children
    }
  ),
  h6: ({ children, className, ...props }) => /* @__PURE__ */ jsx("h6", { className: cn("mt-6 mb-2 text-sm font-semibold", className), ...props, children }),
  table: ({ children, className, ...props }) => /* @__PURE__ */ jsx("div", { className: "my-4 overflow-x-auto", children: /* @__PURE__ */ jsx(
    "table",
    {
      className: cn("w-full border-collapse border border-border", className),
      ...props,
      children
    }
  ) }),
  thead: ({ children, className, ...props }) => /* @__PURE__ */ jsx("thead", { className: cn("bg-muted/50", className), ...props, children }),
  tbody: ({ children, className, ...props }) => /* @__PURE__ */ jsx("tbody", { className: cn("divide-y divide-border", className), ...props, children }),
  tr: ({ children, className, ...props }) => /* @__PURE__ */ jsx("tr", { className: cn("border-b border-border", className), ...props, children }),
  th: ({ children, className, ...props }) => /* @__PURE__ */ jsx(
    "th",
    {
      className: cn("px-4 py-2 text-left text-sm font-semibold", className),
      ...props,
      children
    }
  ),
  td: ({ children, className, ...props }) => /* @__PURE__ */ jsx("td", { className: cn("px-4 py-2 text-sm", className), ...props, children }),
  blockquote: ({ children, className, ...props }) => /* @__PURE__ */ jsx(
    "blockquote",
    {
      className: cn(
        "my-4 border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground",
        className
      ),
      ...props,
      children
    }
  ),
  code: ({ node, className, ...props }) => {
    const inline = node?.position?.start.line === node?.position?.end.line;
    if (!inline) {
      return /* @__PURE__ */ jsx("code", { className, ...props });
    }
    return /* @__PURE__ */ jsx(
      "code",
      {
        className: cn(
          "rounded bg-muted px-1.5 py-0.5 font-mono text-sm",
          className
        ),
        ...props
      }
    );
  },
  pre: ({ node, className, children }) => {
    let language = "javascript";
    const classNameProp = node?.properties?.className;
    if (typeof classNameProp === "string") {
      language = classNameProp.replace("language-", "");
    } else if (Array.isArray(classNameProp)) {
      const languageClass = classNameProp.find(
        (value) => typeof value === "string" && value.startsWith("language-")
      );
      if (typeof languageClass === "string") {
        language = languageClass.replace("language-", "");
      }
    }
    let code = "";
    if (isValidElement(children) && children.props && typeof children.props.children === "string") {
      code = children.props.children ?? "";
    } else if (typeof children === "string") {
      code = children;
    }
    return /* @__PURE__ */ jsx(
      CodeBlock,
      {
        className: cn("my-4 h-auto", className),
        code,
        language,
        children: /* @__PURE__ */ jsx(CodeBlockCopyButton, {})
      }
    );
  }
};
var Response = memo(
  ({
    className,
    options,
    children,
    allowedImagePrefixes,
    allowedLinkPrefixes,
    defaultOrigin,
    parseIncompleteMarkdown: shouldParseIncompleteMarkdown = true,
    ...props
  }) => {
    const parsedChildren = typeof children === "string" && shouldParseIncompleteMarkdown ? parseIncompleteMarkdown(children) : children;
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
          className
        ),
        ...props,
        children: /* @__PURE__ */ jsx(
          HardenedMarkdown,
          {
            allowedImagePrefixes: allowedImagePrefixes ?? ["*"],
            allowedLinkPrefixes: allowedLinkPrefixes ?? ["*"],
            components,
            defaultOrigin,
            rehypePlugins: [rehypeKatex],
            remarkPlugins: [remarkGfm, remarkMath],
            ...options,
            children: parsedChildren
          }
        )
      }
    );
  },
  (prevProps, nextProps) => prevProps.children === nextProps.children
);
Response.displayName = "Response";
function ChipInput({
  value = [],
  onChange,
  placeholder = "Add item and press Enter...",
  label,
  description,
  disabled = false
}) {
  const [inputValue, setInputValue] = useState("");
  const handleKeyDown = (e) => {
    if (disabled) {
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed && !value.includes(trimmed)) {
        onChange([...value, trimmed]);
        setInputValue("");
      }
    }
  };
  const removeItem = (index) => {
    if (disabled)
      return;
    const newValues = value.filter((_, i) => i !== index);
    onChange(newValues);
  };
  return /* @__PURE__ */ jsxs("div", { className: "grid gap-2", children: [
    label && /* @__PURE__ */ jsx(Label2, { children: label }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2", children: [
      value.map((item, idx) => /* @__PURE__ */ jsxs(
        "div",
        {
          className: "inline-flex items-center gap-1 px-2 py-1 text-sm bg-secondary rounded-md",
          children: [
            /* @__PURE__ */ jsx("span", { children: item }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => removeItem(idx),
                className: "hover:opacity-70 focus:outline-none disabled:opacity-40",
                disabled,
                "aria-label": `Remove ${item}`,
                children: /* @__PURE__ */ jsx(X, { className: "h-3 w-3" })
              }
            )
          ]
        },
        idx
      )),
      /* @__PURE__ */ jsx(
        Input,
        {
          type: "text",
          value: inputValue,
          onChange: (e) => setInputValue(e.target.value),
          onKeyDown: handleKeyDown,
          placeholder: value.length === 0 ? placeholder : "",
          className: "flex-1 min-w-[150px] border-0 p-0 h-auto focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
          disabled
        }
      )
    ] }),
    description && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: description })
  ] });
}
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Continue",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  variant = "default",
  loading = false
}) {
  const handleConfirm = async () => {
    await onConfirm();
  };
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };
  return /* @__PURE__ */ jsx(AlertDialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
    /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
      /* @__PURE__ */ jsx(AlertDialogTitle, { children: title }),
      /* @__PURE__ */ jsx(AlertDialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
      /* @__PURE__ */ jsx(AlertDialogCancel, { onClick: handleCancel, disabled: loading, children: cancelLabel }),
      /* @__PURE__ */ jsx(
        AlertDialogAction,
        {
          onClick: handleConfirm,
          variant,
          disabled: loading,
          children: loading ? "Loading..." : confirmLabel
        }
      )
    ] })
  ] }) });
}
function DataTable({
  columns,
  data,
  isLoading = false,
  pageSize = 50,
  enablePagination = false
}) {
  const [sorting, setSorting] = React10__default.useState([]);
  const [pagination, setPagination] = React10__default.useState({
    pageIndex: 0,
    pageSize
  });
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...enablePagination ? { pagination } : {}
    },
    onSortingChange: setSorting,
    ...enablePagination ? { onPaginationChange: setPagination } : {},
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...enablePagination ? { getPaginationRowModel: getPaginationRowModel() } : {}
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsx("div", { className: "rounded-md border", children: /* @__PURE__ */ jsxs(Table, { children: [
      /* @__PURE__ */ jsx(TableHeader, { children: table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsx(TableRow, { children: headerGroup.headers.map((header) => /* @__PURE__ */ jsxs(
        TableHead,
        {
          className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
          onClick: header.column.getToggleSortingHandler(),
          children: [
            flexRender(
              header.column.columnDef.header,
              header.getContext()
            ),
            {
              asc: " \u2191",
              desc: " \u2193"
            }[header.column.getIsSorted()] ?? null
          ]
        },
        header.id
      )) }, headerGroup.id)) }),
      /* @__PURE__ */ jsx(TableBody, { children: isLoading ? /* @__PURE__ */ jsx(TableRow, { children: /* @__PURE__ */ jsx(TableCell, { colSpan: columns.length, className: "text-center py-6", children: "Loading data..." }) }) : table.getRowModel().rows.length ? table.getRowModel().rows.map((row) => /* @__PURE__ */ jsx(TableRow, { children: row.getVisibleCells().map((cell) => /* @__PURE__ */ jsx(TableCell, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id)) }, row.id)) : /* @__PURE__ */ jsx(TableRow, { children: /* @__PURE__ */ jsx(TableCell, { colSpan: columns.length, className: "py-6 text-center", children: "No results found." }) }) })
    ] }) }),
    enablePagination && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "text-sm text-muted-foreground", children: [
        "Showing ",
        table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1,
        " to",
        " ",
        Math.min(
          (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
          table.getFilteredRowModel().rows.length
        ),
        " ",
        "of ",
        table.getFilteredRowModel().rows.length,
        " entries"
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center space-x-2", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: () => table.setPageIndex(0),
            disabled: !table.getCanPreviousPage(),
            children: /* @__PURE__ */ jsx(ChevronsLeft, { className: "h-4 w-4" })
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: () => table.previousPage(),
            disabled: !table.getCanPreviousPage(),
            children: /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" })
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "flex items-center gap-1", children: /* @__PURE__ */ jsxs("div", { className: "text-sm font-medium", children: [
          "Page ",
          table.getState().pagination.pageIndex + 1,
          " of",
          " ",
          table.getPageCount()
        ] }) }),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: () => table.nextPage(),
            disabled: !table.getCanNextPage(),
            children: /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: () => table.setPageIndex(table.getPageCount() - 1),
            disabled: !table.getCanNextPage(),
            children: /* @__PURE__ */ jsx(ChevronsRight, { className: "h-4 w-4" })
          }
        )
      ] })
    ] })
  ] });
}
function InstructionsWidget({ title, content, filename = "instructions" }) {
  const [isOpen, setIsOpen] = useState(false);
  const { toast: toast2 } = useToast();
  const handleDownload = () => {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${(/* @__PURE__ */ new Date()).toISOString().split("T")[0]}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast2({ title: "Instructions downloaded" });
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast2({ title: "Instructions copied to clipboard" });
    } catch {
      toast2({
        title: "Failed to copy",
        variant: "destructive"
      });
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "outline",
        size: "sm",
        onClick: () => setIsOpen(true),
        className: "gap-2",
        children: [
          /* @__PURE__ */ jsx(HelpCircle, { className: "h-4 w-4" }),
          "Help"
        ]
      }
    ),
    /* @__PURE__ */ jsx(Dialog, { open: isOpen, onOpenChange: setIsOpen, children: /* @__PURE__ */ jsxs(DialogContent, { className: "max-w-4xl max-h-[80vh] overflow-hidden flex flex-col", children: [
      /* @__PURE__ */ jsx(DialogHeader, { className: "flex-shrink-0", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between pr-8", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(DialogTitle, { children: title }),
          /* @__PURE__ */ jsxs(Badge, { variant: "secondary", className: "text-xs flex items-center gap-1", children: [
            /* @__PURE__ */ jsx(Package, { className: "h-3 w-3" }),
            "shared-ui v1.0.0-alpha"
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
          /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "outline",
              size: "sm",
              onClick: handleCopy,
              className: "gap-2",
              children: [
                /* @__PURE__ */ jsx(Copy, { className: "h-4 w-4" }),
                "Copy"
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "outline",
              size: "sm",
              onClick: handleDownload,
              className: "gap-2",
              children: [
                /* @__PURE__ */ jsx(Download, { className: "h-4 w-4" }),
                "Download"
              ]
            }
          )
        ] })
      ] }) }),
      /* @__PURE__ */ jsx("div", { className: "overflow-y-auto flex-1 pr-4", children: /* @__PURE__ */ jsx("div", { className: "prose prose-sm max-w-none", children: /* @__PURE__ */ jsx(
        "div",
        {
          dangerouslySetInnerHTML: {
            __html: content.split("\n#").map((section, i) => {
              if (i === 0)
                return section;
              return `<h3>${section.substring(0, section.indexOf("\n"))}</h3>${section.substring(section.indexOf("\n"))}`;
            }).join("").replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>").replace(/^/, "<p>").replace(/$/, "</p>").replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\*(.*?)\*/g, "<em>$1</em>").replace(/`(.*?)`/g, "<code>$1</code>").replace(/- (.*?)(<br\/>|<\/p>)/g, "<li>$1</li>").replace(/(<li>.*<\/li>)+/g, "<ul>$&</ul>").replace(/<\/li><li>/g, "</li><li>")
          }
        }
      ) }) })
    ] }) })
  ] });
}
function StatusBadge({ count: count2, severity, label }) {
  if (count2 === 0 || severity === "success") {
    return /* @__PURE__ */ jsxs(
      Badge,
      {
        variant: "outline",
        className: "text-green-600 bg-green-50 border-green-200",
        role: "status",
        "aria-label": label || "Status: Success",
        children: [
          /* @__PURE__ */ jsx(CheckCircle, { className: "h-3 w-3 mr-1", "aria-hidden": "true" }),
          label || "Valid"
        ]
      }
    );
  }
  const getSeverityDisplay = () => {
    switch (severity) {
      case "high":
        return {
          icon: /* @__PURE__ */ jsx(AlertCircle, { className: "h-3 w-3 mr-1", "aria-hidden": "true" }),
          className: "text-red-600 bg-red-50 border-red-200"
        };
      case "medium":
        return {
          icon: /* @__PURE__ */ jsx(AlertTriangle, { className: "h-3 w-3 mr-1", "aria-hidden": "true" }),
          className: "text-orange-600 bg-orange-50 border-orange-200"
        };
      case "low":
        return {
          icon: /* @__PURE__ */ jsx(Info, { className: "h-3 w-3 mr-1", "aria-hidden": "true" }),
          className: "text-yellow-600 bg-yellow-50 border-yellow-200"
        };
      default:
        return {
          icon: /* @__PURE__ */ jsx(AlertCircle, { className: "h-3 w-3 mr-1", "aria-hidden": "true" }),
          className: "text-red-600 bg-red-50 border-red-200"
        };
    }
  };
  const display = getSeverityDisplay();
  const defaultLabel = count2 === 1 ? "item" : "items";
  const statusLabel = label || `${count2} ${defaultLabel}${severity ? ` with ${severity} severity` : ""}`;
  return /* @__PURE__ */ jsxs(
    Badge,
    {
      variant: "outline",
      className: `${display.className} font-medium`,
      role: "status",
      "aria-label": statusLabel,
      children: [
        display.icon,
        label || `${count2} ${defaultLabel}`
      ]
    }
  );
}
function DebugIndicator({
  storageKey = "debugModeEnabled",
  label = "Debug Mode Active",
  position = "bottom-right"
}) {
  const [isDebugMode, setIsDebugMode] = useState(false);
  useEffect(() => {
    const debugModeEnabled = sessionStorage.getItem(storageKey) === "true";
    setIsDebugMode(debugModeEnabled);
    const handleStorageChange = () => {
      const debugModeEnabled2 = sessionStorage.getItem(storageKey) === "true";
      setIsDebugMode(debugModeEnabled2);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [storageKey]);
  if (!isDebugMode)
    return null;
  const positionClasses = {
    "bottom-right": "bottom-2.5 right-2.5",
    "bottom-left": "bottom-2.5 left-2.5",
    "top-right": "top-2.5 right-2.5",
    "top-left": "top-2.5 left-2.5"
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: `fixed ${positionClasses[position]} px-2.5 py-1.5 bg-orange-500/80 text-white rounded text-xs z-[9999] pointer-events-none`,
      role: "status",
      "aria-live": "polite",
      children: label
    }
  );
}
function StatusSummaryCards({
  cards,
  columns = 4,
  className = ""
}) {
  const gridCols = {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    5: "md:grid-cols-5",
    6: "md:grid-cols-6"
  };
  return /* @__PURE__ */ jsx("div", { className: `grid gap-4 ${gridCols[columns]} ${className}`, children: cards.map((card, index) => /* @__PURE__ */ jsxs(Card, { className: card.className, children: [
    /* @__PURE__ */ jsx(CardHeader, { className: `pb-2 ${card.headerClassName || ""}`, children: /* @__PURE__ */ jsx(CardTitle, { className: "text-sm font-medium", children: card.title }) }),
    /* @__PURE__ */ jsxs(CardContent, { className: card.contentClassName, children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx("div", { className: "text-2xl font-bold", children: card.total ? `${card.value}/${card.total}` : card.value }),
        card.icon && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: typeof card.icon === "function" ? /* @__PURE__ */ jsx(card.icon, { className: "h-5 w-5" }) : card.icon })
      ] }),
      card.progress && /* @__PURE__ */ jsx(
        Progress,
        {
          value: card.progress.value,
          className: `mt-2 ${card.progress.className || ""}`
        }
      ),
      card.details && card.details.length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-2 space-y-1", children: card.details.map((detail, detailIndex) => /* @__PURE__ */ jsxs(
        "div",
        {
          className: `flex items-center text-xs ${detail.className || "text-gray-600"}`,
          children: [
            detail.icon && /* @__PURE__ */ jsx("div", { className: "mr-2 flex-shrink-0", children: detail.icon }),
            /* @__PURE__ */ jsx("span", { children: detail.label })
          ]
        },
        detailIndex
      )) })
    ] })
  ] }, index)) });
}
function ErrorsIndicator({
  errors,
  noErrorsMessage = "No Issues",
  noErrorsIcon,
  className = "",
  showSuccessState = true
}) {
  const activeErrors = errors.filter((error) => error.count > 0);
  if (activeErrors.length === 0 && showSuccessState) {
    return /* @__PURE__ */ jsxs("div", { className: `flex items-center gap-1 ${className}`, children: [
      noErrorsIcon || /* @__PURE__ */ jsx(CheckCircle2, { className: "h-4 w-4 text-green-600" }),
      /* @__PURE__ */ jsx("span", { className: "text-sm text-green-600", children: noErrorsMessage })
    ] });
  }
  if (activeErrors.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx("div", { className: `flex items-center gap-2 ${className}`, children: activeErrors.map((error) => {
    if (error.severity !== void 0) {
      const wrapper = error.onClick ? /* @__PURE__ */ jsx(
        "div",
        {
          className: "inline-block cursor-pointer hover:opacity-80",
          onClick: error.onClick,
          children: /* @__PURE__ */ jsx(
            StatusBadge,
            {
              count: error.count,
              severity: error.severity,
              label: error.label
            }
          )
        },
        error.id
      ) : /* @__PURE__ */ jsx(
        StatusBadge,
        {
          count: error.count,
          severity: error.severity,
          label: error.label
        },
        error.id
      );
      return wrapper;
    }
    const badge = /* @__PURE__ */ jsxs(
      Badge,
      {
        variant: error.variant || "destructive",
        className: `${error.className || "bg-red-100 text-red-700"} ${error.onClick ? "cursor-pointer hover:bg-red-200" : ""}`,
        onClick: error.onClick,
        children: [
          error.icon && /* @__PURE__ */ jsx("span", { className: "mr-1", children: error.icon }),
          error.label
        ]
      },
      error.id
    );
    return badge;
  }) });
}
var InputWithIcons = React10.forwardRef(
  ({
    className,
    type,
    startIcon: StartIcon,
    endIcon,
    startIconClassName,
    endIconClassName,
    ...props
  }, ref) => {
    const EndIconComponent = endIcon && typeof endIcon !== "function" ? null : endIcon;
    return /* @__PURE__ */ jsxs("div", { className: "relative w-full", children: [
      StartIcon && /* @__PURE__ */ jsx("div", { className: "absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none", children: /* @__PURE__ */ jsx(
        StartIcon,
        {
          className: cn("h-4 w-4 text-muted-foreground", startIconClassName)
        }
      ) }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type,
          className: cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            StartIcon && "pl-10",
            endIcon && "pr-10",
            className
          ),
          ref,
          ...props
        }
      ),
      endIcon && /* @__PURE__ */ jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none", children: EndIconComponent ? /* @__PURE__ */ jsx(
        EndIconComponent,
        {
          className: cn("h-4 w-4 text-muted-foreground", endIconClassName)
        }
      ) : /* @__PURE__ */ jsx(Fragment, { children: endIcon }) })
    ] });
  }
);
InputWithIcons.displayName = "InputWithIcons";

// src/components/ui-umd/renderers/registry.ts
var rendererCatalog = [];
var rendererMap = /* @__PURE__ */ new Map();
function registerScreenRenderer(meta) {
  rendererMap.set(meta.id, meta);
  const existingIndex = rendererCatalog.findIndex((renderer) => renderer.id === meta.id);
  if (existingIndex >= 0) {
    rendererCatalog[existingIndex] = meta;
  } else {
    rendererCatalog.push(meta);
  }
}
function listScreenRenderers() {
  return rendererCatalog.map((renderer) => ({ ...renderer }));
}
function getScreenRenderer(id) {
  const matched = rendererMap.get(id);
  if (!matched) {
    throw new Error(`Unknown analysis screen renderer: ${id}`);
  }
  return matched;
}

// src/components/ui-umd/renderers/shared-utils.ts
var capitalize = (value) => {
  if (!value) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};
var DECISION_LABELS = {
  acceptable: "Acceptable",
  conditional: "Conditional",
  reject: "Reject"
};
var DECISION_CLASSES = {
  acceptable: "bg-emerald-100 text-emerald-700 border-emerald-200",
  conditional: "bg-amber-100 text-amber-700 border-amber-200",
  reject: "bg-red-100 text-red-700 border-red-200"
};
var getDecisionDisplay = (decision) => {
  if (!decision) {
    return {
      label: "No Decision",
      className: "bg-slate-100 text-slate-600 border-slate-200"
    };
  }
  const normalized = decision.toLowerCase();
  return {
    label: DECISION_LABELS[normalized] ?? capitalize(normalized),
    className: DECISION_CLASSES[normalized] ?? "bg-slate-100 text-slate-600 border-slate-200"
  };
};
var formatConfidence = (confidence) => {
  if (!confidence) {
    return "Not provided";
  }
  return capitalize(confidence);
};
var getRiskDisplay = (score) => {
  if (typeof score !== "number") {
    return {
      label: "No Score",
      badgeClass: "bg-slate-100 text-slate-600 border-slate-200",
      meterClass: "bg-slate-100 text-slate-700"
    };
  }
  if (score >= 4) {
    return {
      label: "High Risk",
      badgeClass: "bg-red-100 text-red-700 border-red-200",
      meterClass: "bg-red-600 text-white"
    };
  }
  if (score === 3) {
    return {
      label: "Medium Risk",
      badgeClass: "bg-amber-100 text-amber-700 border-amber-200",
      meterClass: "bg-amber-500 text-white"
    };
  }
  return {
    label: "Low Risk",
    badgeClass: "bg-emerald-100 text-emerald-700 border-emerald-200",
    meterClass: "bg-emerald-600 text-white"
  };
};

// src/components/editor/themes/editor-theme.ts
var editorTheme = {
  ltr: "text-left",
  rtl: "text-right",
  heading: {
    h1: "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
    h2: "scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0",
    h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
    h4: "scroll-m-20 text-xl font-semibold tracking-tight",
    h5: "scroll-m-20 text-lg font-semibold tracking-tight",
    h6: "scroll-m-20 text-base font-semibold tracking-tight"
  },
  paragraph: "leading-7 [&:not(:first-child)]:mt-6",
  quote: "mt-6 border-l-2 pl-6 italic",
  link: "text-blue-600 hover:underline hover:cursor-pointer",
  list: {
    checklist: "relative",
    listitem: "mx-8",
    listitemChecked: 'relative mx-2 px-6 list-none outline-none line-through before:content-[""] before:w-4 before:h-4 before:top-0.5 before:left-0 before:cursor-pointer before:block before:bg-cover before:absolute before:border before:border-primary before:rounded before:bg-primary before:bg-no-repeat after:content-[""] after:cursor-pointer after:border-white after:border-solid after:absolute after:block after:top-[6px] after:w-[3px] after:left-[7px] after:right-[7px] after:h-[6px] after:rotate-45 after:border-r-2 after:border-b-2 after:border-l-0 after:border-t-0',
    listitemUnchecked: 'relative mx-2 px-6 list-none outline-none before:content-[""] before:w-4 before:h-4 before:top-0.5 before:left-0 before:cursor-pointer before:block before:bg-cover before:absolute before:border before:border-primary before:rounded',
    nested: {
      listitem: "list-none before:hidden after:hidden"
    },
    ol: "m-0 p-0 list-decimal [&>li]:mt-2",
    olDepth: [
      "list-outside !list-decimal",
      "list-outside !list-[upper-roman]",
      "list-outside !list-[lower-roman]",
      "list-outside !list-[upper-alpha]",
      "list-outside !list-[lower-alpha]"
    ],
    ul: "m-0 p-0 list-outside [&>li]:mt-2",
    ulDepth: [
      "list-outside !list-disc",
      "list-outside !list-disc",
      "list-outside !list-disc",
      "list-outside !list-disc",
      "list-outside !list-disc"
    ]
  },
  hashtag: "text-blue-600 bg-blue-100 rounded-md px-1",
  text: {
    bold: "font-bold",
    code: "bg-gray-100 p-1 rounded-md",
    italic: "italic",
    strikethrough: "line-through",
    subscript: "sub",
    superscript: "sup",
    underline: "underline",
    underlineStrikethrough: "underline line-through"
  },
  image: "relative inline-block user-select-none cursor-default editor-image",
  inlineImage: "relative inline-block user-select-none cursor-default inline-editor-image",
  keyword: "text-purple-900 font-bold",
  code: "EditorTheme__code",
  codeHighlight: {
    atrule: "EditorTheme__tokenAttr",
    attr: "EditorTheme__tokenAttr",
    boolean: "EditorTheme__tokenProperty",
    builtin: "EditorTheme__tokenSelector",
    cdata: "EditorTheme__tokenComment",
    char: "EditorTheme__tokenSelector",
    class: "EditorTheme__tokenFunction",
    "class-name": "EditorTheme__tokenFunction",
    comment: "EditorTheme__tokenComment",
    constant: "EditorTheme__tokenProperty",
    deleted: "EditorTheme__tokenProperty",
    doctype: "EditorTheme__tokenComment",
    entity: "EditorTheme__tokenOperator",
    function: "EditorTheme__tokenFunction",
    important: "EditorTheme__tokenVariable",
    inserted: "EditorTheme__tokenSelector",
    keyword: "EditorTheme__tokenAttr",
    namespace: "EditorTheme__tokenVariable",
    number: "EditorTheme__tokenProperty",
    operator: "EditorTheme__tokenOperator",
    prolog: "EditorTheme__tokenComment",
    property: "EditorTheme__tokenProperty",
    punctuation: "EditorTheme__tokenPunctuation",
    regex: "EditorTheme__tokenVariable",
    selector: "EditorTheme__tokenSelector",
    string: "EditorTheme__tokenSelector",
    symbol: "EditorTheme__tokenProperty",
    tag: "EditorTheme__tokenProperty",
    url: "EditorTheme__tokenOperator",
    variable: "EditorTheme__tokenVariable"
  },
  characterLimit: "!bg-destructive/50",
  table: "EditorTheme__table w-fit overflow-scroll border-collapse",
  tableCell: 'EditorTheme__tableCell w-24 relative border px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right"',
  tableCellActionButton: "EditorTheme__tableCellActionButton bg-background block border-0 rounded-2xl w-5 h-5 text-foreground cursor-pointer",
  tableCellActionButtonContainer: "EditorTheme__tableCellActionButtonContainer block right-1 top-1.5 absolute z-10 w-5 h-5",
  tableCellEditing: "EditorTheme__tableCellEditing rounded-sm shadow-sm",
  tableCellHeader: "EditorTheme__tableCellHeader bg-muted border px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right",
  tableCellPrimarySelected: "EditorTheme__tableCellPrimarySelected border border-primary border-solid block h-[calc(100%-2px)] w-[calc(100%-2px)] absolute -left-[1px] -top-[1px] z-10 ",
  tableCellResizer: "EditorTheme__tableCellResizer absolute -right-1 h-full w-2 cursor-ew-resize z-10 top-0",
  tableCellSelected: "EditorTheme__tableCellSelected bg-muted",
  tableCellSortedIndicator: "EditorTheme__tableCellSortedIndicator block opacity-50 bsolute bottom-0 left-0 w-full h-1 bg-muted",
  tableResizeRuler: "EditorTheme__tableCellResizeRuler block absolute w-[1px] h-full bg-primary top-0",
  tableRowStriping: "EditorTheme__tableRowStriping m-0 border-t p-0 even:bg-muted",
  tableSelected: "EditorTheme__tableSelected ring-2 ring-primary ring-offset-2",
  tableSelection: "EditorTheme__tableSelection bg-transparent",
  layoutItem: "border border-dashed px-4 py-2",
  layoutContainer: "grid gap-2.5 my-2.5 mx-0",
  autocomplete: "text-muted-foreground",
  blockCursor: "",
  embedBlock: {
    base: "user-select-none",
    focus: "ring-2 ring-primary ring-offset-2"
  },
  hr: 'p-0.5 border-none my-1 mx-0 cursor-pointer after:content-[""] after:block after:h-0.5 after:bg-muted selected:ring-2 selected:ring-primary selected:ring-offset-2 selected:user-select-none',
  indent: "[--lexical-indent-base-value:40px]",
  mark: "",
  markOverlap: ""
};
var nodes = [
  HeadingNode,
  ParagraphNode,
  TextNode,
  QuoteNode,
  CodeNode,
  LinkNode,
  ListNode,
  ListItemNode
];
function ContentEditable({
  placeholder,
  className,
  placeholderClassName
}) {
  return /* @__PURE__ */ jsx(
    ContentEditable$1,
    {
      className: className ?? `ContentEditable__root relative block min-h-72 min-h-full overflow-auto px-8 py-4 focus:outline-none`,
      "aria-placeholder": placeholder,
      placeholder: /* @__PURE__ */ jsx(
        "div",
        {
          className: placeholderClassName ?? `text-muted-foreground pointer-events-none absolute top-0 left-0 overflow-hidden px-8 py-[18px] text-ellipsis select-none`,
          children: placeholder
        }
      )
    }
  );
}

// src/components/editor/plugins/actions/actions-plugin.tsx
function ActionsPlugin({ children }) {
  return children;
}
function MarkdownTogglePlugin({
  shouldPreserveNewLinesInMarkdown,
  transformers
}) {
  const [editor] = useLexicalComposerContext();
  const handleMarkdownToggle = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      const firstChild = root.getFirstChild();
      if ($isCodeNode(firstChild) && firstChild.getLanguage() === "markdown") {
        $convertFromMarkdownString(
          firstChild.getTextContent(),
          transformers,
          void 0,
          // node
          shouldPreserveNewLinesInMarkdown
        );
      } else {
        const markdown = $convertToMarkdownString(
          transformers,
          void 0,
          //node
          shouldPreserveNewLinesInMarkdown
        );
        const codeNode = $createCodeNode("markdown");
        codeNode.append($createTextNode(markdown));
        root.clear().append(codeNode);
        if (markdown.length === 0) {
          codeNode.select();
        }
      }
    });
  }, [editor, shouldPreserveNewLinesInMarkdown]);
  return /* @__PURE__ */ jsx(
    Button,
    {
      variant: "ghost",
      onClick: handleMarkdownToggle,
      title: "Convert From Markdown",
      "aria-label": "Convert from markdown",
      size: "sm",
      className: "p-2",
      children: /* @__PURE__ */ jsx(FileTextIcon, { className: "size-4" })
    }
  );
}
var Context = createContext({
  activeEditor: {},
  $updateToolbar: () => {
  },
  blockType: "paragraph",
  setBlockType: () => {
  },
  showModal: () => {
  }
});
function ToolbarContext({
  activeEditor,
  $updateToolbar,
  blockType,
  setBlockType,
  showModal,
  children
}) {
  return /* @__PURE__ */ jsx(
    Context.Provider,
    {
      value: {
        activeEditor,
        $updateToolbar,
        blockType,
        setBlockType,
        showModal
      },
      children
    }
  );
}
function useToolbarContext() {
  return useContext(Context);
}
function useUpdateToolbarHandler(callback) {
  const [editor] = useLexicalComposerContext();
  const { activeEditor } = useToolbarContext();
  useEffect(() => {
    return activeEditor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection();
        if (selection) {
          callback(selection);
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, callback]);
  useEffect(() => {
    activeEditor.getEditorState().read(() => {
      const selection = $getSelection();
      if (selection) {
        callback(selection);
      }
    });
  }, [activeEditor, callback]);
}
function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
}
var toggleVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 gap-2",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        default: "h-10 px-3 min-w-10",
        sm: "h-9 px-2.5 min-w-9",
        lg: "h-11 px-5 min-w-11"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Toggle = React10.forwardRef(({ className, variant, size, ...props }, ref) => /* @__PURE__ */ jsx(
  TogglePrimitive.Root,
  {
    ref,
    className: cn(toggleVariants({ variant, size, className })),
    ...props
  }
));
Toggle.displayName = TogglePrimitive.Root.displayName;
var ToggleGroupContext = React10.createContext({
  size: "default",
  variant: "default"
});
var ToggleGroup = React10.forwardRef(({ className, variant, size, children, ...props }, ref) => /* @__PURE__ */ jsx(
  ToggleGroupPrimitive.Root,
  {
    ref,
    className: cn("flex items-center justify-center gap-1", className),
    ...props,
    children: /* @__PURE__ */ jsx(ToggleGroupContext.Provider, { value: { variant, size }, children })
  }
));
ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;
var ToggleGroupItem = React10.forwardRef(({ className, children, variant, size, ...props }, ref) => {
  const context = React10.useContext(ToggleGroupContext);
  return /* @__PURE__ */ jsx(
    ToggleGroupPrimitive.Item,
    {
      ref,
      className: cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size
        }),
        className
      ),
      ...props,
      children
    }
  );
});
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;
var ELEMENT_FORMAT_OPTIONS = {
  left: {
    icon: /* @__PURE__ */ jsx(AlignLeftIcon, { className: "size-4" }),
    iconRTL: "left-align",
    name: "Left Align"
  },
  center: {
    icon: /* @__PURE__ */ jsx(AlignCenterIcon, { className: "size-4" }),
    iconRTL: "center-align",
    name: "Center Align"
  },
  right: {
    icon: /* @__PURE__ */ jsx(AlignRightIcon, { className: "size-4" }),
    iconRTL: "right-align",
    name: "Right Align"
  },
  justify: {
    icon: /* @__PURE__ */ jsx(AlignJustifyIcon, { className: "size-4" }),
    iconRTL: "justify-align",
    name: "Justify Align"
  }
};
function ElementFormatToolbarPlugin({
  separator = true
}) {
  const { activeEditor } = useToolbarContext();
  const [elementFormat, setElementFormat] = useState("left");
  const $updateToolbar = (selection) => {
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      let matchingParent;
      if ($isLinkNode(parent)) {
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
        );
      }
      setElementFormat(
        $isElementNode(matchingParent) ? matchingParent.getFormatType() : $isElementNode(node) ? node.getFormatType() : parent?.getFormatType() || "left"
      );
    }
  };
  useUpdateToolbarHandler($updateToolbar);
  const handleValueChange = (value) => {
    if (!value)
      return;
    setElementFormat(value);
    if (value === "indent") {
      activeEditor.dispatchCommand(INDENT_CONTENT_COMMAND, void 0);
    } else if (value === "outdent") {
      activeEditor.dispatchCommand(OUTDENT_CONTENT_COMMAND, void 0);
    } else {
      activeEditor.dispatchCommand(
        FORMAT_ELEMENT_COMMAND,
        value
      );
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ToggleGroup,
      {
        type: "single",
        value: elementFormat,
        defaultValue: elementFormat,
        onValueChange: handleValueChange,
        className: "flex items-center gap-1.5 rounded-md border border-border/60 bg-background/80 px-1.5 py-1 shadow-sm",
        children: Object.entries(ELEMENT_FORMAT_OPTIONS).map(([value, option]) => /* @__PURE__ */ jsx(
          ToggleGroupItem,
          {
            value,
            variant: "outline",
            size: "sm",
            "aria-label": option.name,
            className: "h-8 w-8 p-0 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary",
            children: option.icon
          },
          value
        ))
      }
    ),
    separator && /* @__PURE__ */ jsx(Separator3, { orientation: "vertical", className: "!h-7" }),
    /* @__PURE__ */ jsxs(
      ToggleGroup,
      {
        type: "single",
        value: elementFormat,
        defaultValue: elementFormat,
        onValueChange: handleValueChange,
        className: "flex items-center gap-1.5 rounded-md border border-border/60 bg-background/80 px-1.5 py-1 shadow-sm",
        children: [
          /* @__PURE__ */ jsx(
            ToggleGroupItem,
            {
              value: "outdent",
              "aria-label": "Outdent",
              variant: "outline",
              size: "sm",
              className: "h-8 w-8 p-0 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary",
              children: /* @__PURE__ */ jsx(IndentDecreaseIcon, { className: "size-4" })
            }
          ),
          /* @__PURE__ */ jsx(
            ToggleGroupItem,
            {
              value: "indent",
              variant: "outline",
              "aria-label": "Indent",
              size: "sm",
              className: "h-8 w-8 p-0 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary",
              children: /* @__PURE__ */ jsx(IndentIncreaseIcon, { className: "size-4" })
            }
          )
        ]
      }
    )
  ] });
}
var TEXT_FORMAT_BUTTONS = [
  { format: "bold", icon: /* @__PURE__ */ jsx(Bold, { className: "size-4" }), name: "Bold", shortcut: "Ctrl+B" },
  { format: "italic", icon: /* @__PURE__ */ jsx(Italic, { className: "size-4" }), name: "Italic", shortcut: "Ctrl+I" },
  { format: "underline", icon: /* @__PURE__ */ jsx(Underline, { className: "size-4" }), name: "Underline", shortcut: "Ctrl+U" },
  { format: "strikethrough", icon: /* @__PURE__ */ jsx(Strikethrough, { className: "size-4" }), name: "Strikethrough" },
  { format: "code", icon: /* @__PURE__ */ jsx(Code, { className: "size-4" }), name: "Code" }
];
var BLOCK_TYPE_OPTIONS = [
  { value: "paragraph", label: "Normal" },
  { value: "h1", label: "Heading 1", icon: /* @__PURE__ */ jsx(Heading1, { className: "size-4" }) },
  { value: "h2", label: "Heading 2", icon: /* @__PURE__ */ jsx(Heading2, { className: "size-4" }) },
  { value: "h3", label: "Heading 3", icon: /* @__PURE__ */ jsx(Heading3, { className: "size-4" }) },
  { value: "ul", label: "Bullet List", icon: /* @__PURE__ */ jsx(List, { className: "size-4" }) },
  { value: "ol", label: "Numbered List", icon: /* @__PURE__ */ jsx(ListOrdered, { className: "size-4" }) }
];
function TextFormatToolbarPlugin({ separator = true }) {
  const { activeEditor } = useToolbarContext();
  const [activeFormats, setActiveFormats] = useState([]);
  const [blockType, setBlockType] = useState("paragraph");
  const $updateToolbar = (selection) => {
    if ($isRangeSelection(selection)) {
      const formats = [];
      if (selection.hasFormat("bold"))
        formats.push("bold");
      if (selection.hasFormat("italic"))
        formats.push("italic");
      if (selection.hasFormat("underline"))
        formats.push("underline");
      if (selection.hasFormat("strikethrough"))
        formats.push("strikethrough");
      if (selection.hasFormat("code"))
        formats.push("code");
      setActiveFormats(formats);
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        const type = element.getType();
        if (type === "heading") {
          const tag = element.getTag?.();
          setBlockType(tag || "paragraph");
        } else if (type === "list") {
          const parentList = element.__parent;
          setBlockType(parentList?.__listType === "number" ? "ol" : "ul");
        } else {
          setBlockType("paragraph");
        }
      }
    }
  };
  useUpdateToolbarHandler($updateToolbar);
  const formatText = (format) => {
    activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };
  const formatBlock = (value) => {
    setBlockType(value);
    if (value === "ul") {
      activeEditor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, void 0);
    } else if (value === "ol") {
      activeEditor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, void 0);
    } else {
      activeEditor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if (value === "paragraph") {
            $setBlocksType(selection, () => $createParagraphNode());
          } else if (value.startsWith("h")) {
            $setBlocksType(selection, () => $createHeadingNode(value));
          }
        }
      });
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Select, { value: blockType, onValueChange: formatBlock, children: [
      /* @__PURE__ */ jsx(
        SelectTrigger,
        {
          "aria-label": "Text style",
          className: "h-9 w-[160px] justify-between rounded-md border border-border/60 bg-background px-3 text-xs font-medium shadow-sm",
          children: /* @__PURE__ */ jsx(SelectValue, { placeholder: "Text style" })
        }
      ),
      /* @__PURE__ */ jsx(SelectContent, { children: BLOCK_TYPE_OPTIONS.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option.value, className: "text-xs", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        option.icon && option.icon,
        /* @__PURE__ */ jsx("span", { children: option.label })
      ] }) }, option.value)) })
    ] }),
    separator && /* @__PURE__ */ jsx(Separator3, { orientation: "vertical", className: "!h-7" }),
    /* @__PURE__ */ jsx(
      ToggleGroup,
      {
        type: "multiple",
        value: activeFormats,
        className: "flex items-center gap-1.5 rounded-md border border-border/60 bg-background/80 px-1.5 py-1 shadow-sm",
        children: TEXT_FORMAT_BUTTONS.map((option) => /* @__PURE__ */ jsx(
          ToggleGroupItem,
          {
            value: option.format,
            variant: "outline",
            size: "sm",
            "aria-label": option.name,
            title: option.shortcut ?? option.name,
            onClick: () => formatText(option.format),
            className: "h-8 w-8 p-0 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary",
            children: option.icon
          },
          option.format
        ))
      }
    )
  ] });
}
function useEditorModal() {
  const [modalContent, setModalContent] = useState(null);
  const onClose = useCallback(() => {
    setModalContent(null);
  }, []);
  const modal = useMemo(() => {
    if (modalContent === null) {
      return null;
    }
    const { title, content } = modalContent;
    return /* @__PURE__ */ jsx(Dialog, { open: true, onOpenChange: onClose, children: /* @__PURE__ */ jsxs(DialogContent, { children: [
      /* @__PURE__ */ jsx(DialogHeader, { children: /* @__PURE__ */ jsx(DialogTitle, { children: title }) }),
      content
    ] }) });
  }, [modalContent, onClose]);
  const showModal = useCallback(
    (title, getContent, closeOnClickOutside = false) => {
      setModalContent({
        closeOnClickOutside,
        content: getContent(onClose),
        title
      });
    },
    [onClose]
  );
  return [modal, showModal];
}
function ToolbarPlugin({
  children
}) {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState("paragraph");
  const [modal, showModal] = useEditorModal();
  const $updateToolbar = () => {
  };
  useEffect(() => {
    return activeEditor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor]);
  return /* @__PURE__ */ jsxs(
    ToolbarContext,
    {
      activeEditor,
      $updateToolbar,
      blockType,
      setBlockType,
      showModal,
      children: [
        modal,
        children({ blockType })
      ]
    }
  );
}
var TRANSFORMERS = [
  CHECK_LIST,
  LINK,
  ...ELEMENT_TRANSFORMERS,
  ...MULTILINE_ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS
];
function Plugins() {
  const [_floatingAnchorElem, setFloatingAnchorElem] = useState(null);
  const onRef = (elem) => {
    if (elem !== null) {
      setFloatingAnchorElem(elem);
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsx(ToolbarPlugin, { children: () => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 border-b bg-muted/30 px-3 py-2 flex-wrap", children: [
      /* @__PURE__ */ jsx(TextFormatToolbarPlugin, {}),
      /* @__PURE__ */ jsx(Separator3, { orientation: "vertical", className: "!h-6" }),
      /* @__PURE__ */ jsx(ElementFormatToolbarPlugin, {}),
      /* @__PURE__ */ jsx(Separator3, { orientation: "vertical", className: "!h-6" }),
      /* @__PURE__ */ jsx(ActionsPlugin, { children: /* @__PURE__ */ jsx(
        MarkdownTogglePlugin,
        {
          shouldPreserveNewLinesInMarkdown: false,
          transformers: TRANSFORMERS
        }
      ) })
    ] }) }),
    /* @__PURE__ */ jsxs("div", { className: "relative bg-background", children: [
      /* @__PURE__ */ jsx(
        RichTextPlugin,
        {
          contentEditable: /* @__PURE__ */ jsx("div", { className: "min-h-[180px] max-h-[400px] overflow-y-auto", children: /* @__PURE__ */ jsx("div", { className: "px-4 py-3", ref: onRef, children: /* @__PURE__ */ jsx(
            ContentEditable,
            {
              placeholder: "Start typing... Use the toolbar to format your text",
              className: "ContentEditable__root relative block min-h-[160px] focus:outline-none prose prose-sm max-w-none dark:prose-invert",
              placeholderClassName: "text-muted-foreground/60 pointer-events-none absolute top-3 left-4 right-4 overflow-hidden text-ellipsis select-none"
            }
          ) }) }),
          ErrorBoundary: LexicalErrorBoundary
        }
      ),
      /* @__PURE__ */ jsx(ListPlugin, {}),
      /* @__PURE__ */ jsx(MarkdownShortcutPlugin, { transformers: TRANSFORMERS })
    ] })
  ] });
}
var editorConfig = {
  namespace: "Editor",
  theme: editorTheme,
  nodes,
  onError: (error) => {
    console.error(error);
  }
};
function EditorRefPlugin({ onEditorReady }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    onEditorReady?.(editor);
  }, [editor, onEditorReady]);
  return null;
}
function Editor({
  editorState,
  editorSerializedState,
  onChange,
  onSerializedChange,
  onEditorReady
}) {
  return /* @__PURE__ */ jsx("div", { className: "bg-background overflow-hidden rounded-lg border shadow", children: /* @__PURE__ */ jsx(
    LexicalComposer,
    {
      initialConfig: {
        ...editorConfig,
        ...editorState ? { editorState } : {},
        ...editorSerializedState ? { editorState: JSON.stringify(editorSerializedState) } : {}
      },
      children: /* @__PURE__ */ jsxs(TooltipProvider, { children: [
        /* @__PURE__ */ jsx(Plugins, {}),
        /* @__PURE__ */ jsx(EditorRefPlugin, { onEditorReady }),
        /* @__PURE__ */ jsx(
          OnChangePlugin,
          {
            ignoreSelectionChange: true,
            onChange: (editorState2, editor) => {
              onChange?.(editorState2, editor);
              onSerializedChange?.(editorState2.toJSON());
            }
          }
        )
      ] })
    }
  ) });
}
var SOFT_LIMIT = 10240;
var HARD_LIMIT = 20480;
function CharacterCounter({ count: count2 }) {
  const percentage = count2 / HARD_LIMIT * 100;
  const isOverSoft = count2 > SOFT_LIMIT;
  const isOverHard = count2 > HARD_LIMIT;
  const getColor = () => {
    if (isOverHard)
      return "text-destructive";
    if (isOverSoft)
      return "text-yellow-600 dark:text-yellow-500";
    return "text-muted-foreground";
  };
  const getIcon = () => {
    if (isOverHard)
      return /* @__PURE__ */ jsx(XCircle, { className: "h-3 w-3" });
    if (isOverSoft)
      return /* @__PURE__ */ jsx(AlertCircle, { className: "h-3 w-3" });
    return /* @__PURE__ */ jsx(CheckCircle2, { className: "h-3 w-3" });
  };
  const getMessage = () => {
    if (isOverHard) {
      return `${count2.toLocaleString()} / ${HARD_LIMIT.toLocaleString()} characters (exceeds maximum)`;
    }
    if (isOverSoft) {
      return `${count2.toLocaleString()} / ${HARD_LIMIT.toLocaleString()} characters (consider splitting)`;
    }
    return `${count2.toLocaleString()} / ${HARD_LIMIT.toLocaleString()} characters`;
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-xs", children: [
    /* @__PURE__ */ jsxs("div", { className: `flex items-center gap-1.5 ${getColor()}`, children: [
      getIcon(),
      /* @__PURE__ */ jsx("span", { children: getMessage() })
    ] }),
    isOverSoft && /* @__PURE__ */ jsx("div", { className: "h-1.5 w-24 overflow-hidden rounded-full bg-muted", children: /* @__PURE__ */ jsx(
      "div",
      {
        className: `h-full transition-all ${isOverHard ? "bg-destructive" : "bg-yellow-500"}`,
        style: { width: `${Math.min(percentage, 100)}%` }
      }
    ) })
  ] });
}
var HARD_LIMIT2 = 20480;
function MarkdownEditor({
  value,
  onChange,
  onKeyDown,
  showCharacterCount = true
}) {
  const [charCount, setCharCount] = useState(value?.length || 0);
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);
  const lastSyncedValue = useRef(void 0);
  const handleEditorReady = useCallback((editor) => {
    editorRef.current = editor;
  }, []);
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor)
      return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (value === lastSyncedValue.current)
      return;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      if (value) {
        $convertFromMarkdownString(value, TRANSFORMERS);
      }
    });
    lastSyncedValue.current = value;
  }, [value]);
  const initialEditorState = useCallback(() => {
    if (value) {
      $convertFromMarkdownString(value, TRANSFORMERS);
      lastSyncedValue.current = value;
    }
  }, [value]);
  const handleChange = useCallback(
    (editorState) => {
      editorState.read(() => {
        const markdown = $convertToMarkdownString(TRANSFORMERS);
        setCharCount(markdown.length);
        isInternalChange.current = true;
        lastSyncedValue.current = markdown;
        if (markdown.length <= HARD_LIMIT2) {
          onChange?.(markdown);
        }
      });
    },
    [onChange]
  );
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx("div", { onKeyDown, children: /* @__PURE__ */ jsx(
      Editor,
      {
        editorState: initialEditorState,
        onChange: handleChange,
        onEditorReady: handleEditorReady
      }
    ) }),
    showCharacterCount && /* @__PURE__ */ jsx("div", { className: "px-2", children: /* @__PURE__ */ jsx(CharacterCounter, { count: charCount }) })
  ] });
}
function MarkdownViewer({ content }) {
  return /* @__PURE__ */ jsx("div", { className: "prose prose-sm max-w-none dark:prose-invert prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:my-2 prose-blockquote:my-2", children: /* @__PURE__ */ jsx(
    ReactMarkdown,
    {
      remarkPlugins: [remarkGfm],
      components: {
        // Improve table styling
        table: ({ ...props }) => /* @__PURE__ */ jsx("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsx("table", { className: "border-collapse", ...props }) }),
        // Improve code block styling
        code: ({ className, children, ...props }) => {
          const isInline = !className?.includes("language-");
          return isInline ? /* @__PURE__ */ jsx("code", { className: "px-1.5 py-0.5 rounded bg-muted text-sm", ...props, children }) : /* @__PURE__ */ jsx("code", { className, ...props, children });
        }
      },
      children: content
    }
  ) });
}

export { ActionsPlugin, Alert, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AlertTitle, Avatar, AvatarFallback, AvatarImage, Badge, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Checkbox, ChipInput, CodeBlock, CodeBlockCopyButton, Collapsible, CollapsibleContent2 as CollapsibleContent, CollapsibleTrigger2 as CollapsibleTrigger, ConfirmDialog, Conversation, ConversationContent, ConversationScrollButton, DataTable, DebugIndicator, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, Editor, ElementFormatToolbarPlugin, ErrorsIndicator, Input, InputWithIcons, InstructionsWidget, Label2 as Label, MarkdownEditor, MarkdownTogglePlugin, MarkdownViewer, Message, MessageAvatar, MessageContent, Progress, PromptInput, PromptInputButton, PromptInputSubmit, PromptInputTextarea, PromptInputToolbar, PromptInputTools, RadioGroup2 as RadioGroup, RadioGroupItem, Response, ScrollArea, ScrollBar, SearchableSelect, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator3 as Separator, Slider, StatusBadge, StatusSummaryCards, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, Toast, ToastAction, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport, Toaster, ToolbarPlugin, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, badgeVariants, buttonVariants, cn, formatConfidence, getDecisionDisplay, getRiskDisplay, getScreenRenderer, listScreenRenderers, reducer, registerScreenRenderer, toast, useToast };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.js.map